import { Subscription } from '@apollo/client/react/components';
import PatientSlot from '../../../patient/PatientSlot/PatientSlot';
import { getNestedValue } from '@/se/utilities/data/object';
import { getPatientORDuration } from '../../../entities/patient/transducers';
import { parse } from 'date-fns';
import styled from 'styled-components';
import React, { Fragment, useMemo, useState } from 'react';
import { patientPickerTodaySubscription } from '../../../../graph/patients';
import { Error, Exit, Modal, ModalBody, Title } from '../tablet/Modal';
import { compose, mapProps, withProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { withPatientNameFormat } from '../../../../contexts/PatientNameFormat';
import update from 'lodash/update';
import { mapInitialsForPatientList } from '../tablet/mapInitialsForPatientList';
import get from 'lodash/get';
import EntityState from '@/se/components/EntityState';
import Box from '@material-ui/core/Box';
import { Container } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FlexList from '@/se/components/collection/FlexList';
import PatientSlotListView from '../../../entities/room/views/PatientSlotListView';
import { patientEnteredRoomTypeTime } from '../../../entities/patient/util/patientEvents';
import { withScope } from '../../../../contexts/ScopeContext';
import ProcedureHandling from '../tablet/v2/ProcedureHandling';
import { getProcedureState } from '../tablet/util/procedureSteps';
import { useQuery } from '@apollo/client';
import { orTabletProcedureSteps } from '../../../../graph/procedureSteps';
import { STATUSES as ROOM_STATUSES } from '../../../entities/room/enums';

const useStyles = makeStyles({
  container: {
    flex: 1,
    top: 'auto',
    bottom: 'auto',
    overflowY: 'scroll',
  },
});

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0;
`;

const EmptyState = props => (
  <EntityState title={'Waiting for patients.'} hint={`Currently there are no patients to choose from.`} {...props} />
);

const AdmittedPatients = columns =>
  compose(
    withRouter,
    withPatientNameFormat,
    withScope,
    mapProps(({ data, patientNameFormat, scope, ...props }) => ({
      ...props,
      data: update(data, 'patients', patients => {
        if (!patients) {
          return patients;
        }
        return mapInitialsForPatientList(patients, patientNameFormat, scope);
      }),
    })),
    withProps({
      entityName: 'Patient',
      idProvider: item => item.id,
      getList: data => data['patients'],
      columns: columns,
      View: PatientSlotListView,
      hideColumns: true,
      Empty: EmptyState,
    })
  )(FlexList);

const AdmittedPatientsBed = AdmittedPatients([
  {
    lens: data => data,
    Component: ({ data }) => (
      <PatientSlot
        procedure={getNestedValue('procedureType.name', data)}
        physician={getNestedValue('physician.name', data)}
        patient={data.name}
        timeEntered={parse(data.createdAt)}
        preventAnimation={true}
        showRoom={true}
      />
    ),
  },
]);

const AdmittedPatientsRoom = AdmittedPatients([
  {
    lens: data => data,
    Component: ({ data }) => (
      <PatientSlot
        roomName={getNestedValue('room.name', data)}
        procedure={getNestedValue('procedureType.name', data)}
        physician={getNestedValue('physician.name', data)}
        patient={data.name}
        patientType={data.type}
        timeEntered={patientEnteredRoomTypeTime(data, data?.room?.type)}
        preventAnimation={true}
        hideBedInfo={true}
        showRoom={true}
      />
    ),
  },
]);

const filterOpt = (list, fn) => {
  if (list && fn) {
    return fn(list);
  } else {
    return list;
  }
};

export default ({ handleCancel, handlePatientClick, filterFn, TitleAndActions, withRoom, style, room }) => {
  const classes = useStyles();
  const [error, setError] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const handlePatientSelect = async data => {
    try {
      setSelectedPatient(data);
    } catch (error) {
      console.error(error);
      if (error.message.indexOf('Selected entity does not exist')) {
        setError(true);
      }
    }
  };

  const handleCloseProcedureHandling = () => {
    setSelectedPatient(null);
  };

  const onRefresh = refetch => async () => {
    await refetch();
    setError(false);
  };

  const Item = useMemo(() => (withRoom ? AdmittedPatientsRoom : AdmittedPatientsBed), [withRoom]);

  const { data } = useQuery(orTabletProcedureSteps, {
    variables: {
      patientId: selectedPatient?.id,
    },
  });

  const procedureState = useMemo(() => {
    if (selectedPatient?.status === ROOM_STATUSES.CLEANING) {
      return {
        steps: null,
        index: null,
        prev: null,
        next: () => updateRoomStatus(room?.id),
        nextLabel: 'Cleaning Completed',
      };
    }

    const steps = data?.orTabletProcedureSteps || [];
    const state = getProcedureState(steps, selectedPatient?.status);
    return {
      steps: steps.map((e, i) => ({
        ...e,
        highlight: i === (state?.index || 0),
        show: i <= (state?.index || 0),
      })),
      index: state?.index || null,
      prev: null,
      next: null,
      nextLabel: state?.next?.name || null,
    };
  }, [data, selectedPatient?.status, room?.id]);

  return (
    <>
      {selectedPatient ? (
        <>
          <ProcedureHandling
            status={'PROCEDURE_END'}
            room={room}
            duration={getPatientORDuration(selectedPatient)}
            physician={selectedPatient?.physician}
            patient={selectedPatient}
            procedureType={selectedPatient?.procedureType}
            procedureState={procedureState}
            nextProceduresInOperationRoom={[]}
            isPreviousPatient={true}
            previousPatientExit={handleCloseProcedureHandling}
          />
        </>
      ) : (
        <Modal style={style}>
          <Container maxWidth={false} className={classes.container}>
            <Header>
              <Title style={{ fontSize: '1rem' }}>Choose patient to edit</Title>
              <Exit onClick={handleCancel}>Exit</Exit>
            </Header>
            <ModalBody className="modal-body">
              <Subscription subscription={patientPickerTodaySubscription} variables={{ roomId: room?.id }}>
                {props => (
                  <Fragment>
                    {error && (
                      <Error onClick={onRefresh(props.refetch)}>
                        There was a problem with editing the patient. Click here to refresh the list.
                      </Error>
                    )}
                    <Box display="flex" flexDirection="column">
                      <Item
                        onViewClick={handlePatientSelect}
                        TitleAndActions={TitleAndActions}
                        {...props}
                        data={{ patients: filterOpt(get(props, 'data.patients', []), filterFn) }}
                      />
                    </Box>
                  </Fragment>
                )}
              </Subscription>
            </ModalBody>
          </Container>
        </Modal>
      )}
    </>
  );
};
